import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiFileText, FiAlertCircle, FiTrash2, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import CommissionScanClientFiles, { AmsFileSelection } from './CommissionScanClientFiles';
import './CommissionScan.css';
import '../../styles/Voice.css';
import { useToast } from '../../context/ToastContext';

type TabType = 'local' | 'client';

const ACCEPTED_TYPES  = '.pdf,.xls,.xlsx,.csv';
const ACCEPTED_MIME   = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/csv',
];
const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';

interface Props {
  onScanComplete?: (transactionId: string) => void;
  onGoToHistory?: () => void;
  onOpenFeedback?: (transactionId: string) => void;
}

const CommissionScanUpload: React.FC<Props> = ({ onScanComplete, onGoToHistory }) => {
  const navigate = useNavigate();

  /* ── Local Drive tab ── */
  const [activeTab,  setActiveTab]  = useState<TabType>('local');
  const [file,       setFile]       = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef                = useRef<HTMLInputElement>(null);

  /* ── Client Profile tab ── */
  const [selectedClientFile, setSelectedClientFile] = useState<AmsFileSelection | null>(null);

  /* ── Upload state ── */
  const [loading,        setLoading]        = useState(false);
  const [errorMessage,   setErrorMessage]   = useState('');
  const [successMessage, setSuccessMessage] = useState(false);
  const [transactionId,  setTransactionId]  = useState('');

  /* ── Feedback popup ── */
  const [feedbackText,       setFeedbackText]       = useState('');
  const [feedbackRating,     setFeedbackRating]     = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]     = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError,      setFeedBackError]      = useState('');
  const [feedbackSuccess,    setFeedbackSuccess]    = useState(false);
  const { showSuccess } = useToast();
  const feedbackRef = useRef<HTMLDivElement>(null);

  const closeFeedback = useCallback(() => {
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    setFeedbackSuccess(false);
  }, []);

  useEffect(() => {
    if (!feedbackAnchor) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) {
        closeFeedback();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackAnchor, closeFeedback]);

  const saveFeedbackRating = useCallback((isLiked: boolean | null) => {
    if (!transactionId) return;
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 1, TransactionId: transactionId, IsLiked: isLiked, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    ).then(() => {
      showSuccess('Feedback submitted successfully!');
      closeFeedback();
    }).catch((err: any) => {
      if (err.response?.status === 401) navigate('/');
    });
  }, [transactionId, feedbackText, closeFeedback, navigate]);

  const onThumbsUpClick = useCallback(() => {
    const next: boolean | null = feedbackRating === true ? null : true;
    closeFeedback();
    saveFeedbackRating(next);
  }, [feedbackRating, saveFeedbackRating, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    const next: boolean | null = feedbackRating === false ? null : false;
    closeFeedback();
    saveFeedbackRating(next);
  }, [feedbackRating, saveFeedbackRating, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!transactionId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 1, TransactionId: transactionId, IsLiked: feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); setFeedbackSuccess(true); setTimeout(closeFeedback, 1500); })
      .catch((err: any) => { setFeedbackSubmitting(false); if (err.response?.status === 401) navigate('/'); });
  }, [transactionId, feedbackText, feedbackRating, closeFeedback, navigate]);

  const openFeedbackPopup = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    setFeedbackSuccess(false);
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  /* ── File handling ── */
  const pickFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find(
      f => ACCEPTED_MIME.includes(f.type) || /\.(pdf|xls|xlsx|csv)$/i.test(f.name),
    );
    if (first) {
      setFile(first);
      setSuccessMessage(false);
    } else {
      setErrorMessage('Please select a PDF, Excel, or CSV file only');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    pickFile(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) pickFile(e.target.files);
    e.target.value = '';
  };

  const handleCancel = () => {
    setFile(null);
    setSelectedClientFile(null);
    setSuccessMessage(false);
    setErrorMessage('');
    setTransactionId('');
    closeFeedback();
  };

  const createUploadRequest = useCallback(() => {
    const isAms = activeTab === 'client';

    if (!isAms && !file) {
      setErrorMessage('Please select at least one file to upload');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    const ncToken    = localStorage.getItem('amsToken') || '';
    const insuredId  = isAms ? (selectedClientFile?.insuredId || '') : '';
    const folderId   = isAms ? (selectedClientFile?.folderId  || '') : '';
    const amsFileIds = isAms ? [selectedClientFile?.file.id   || ''] : [];

    const selectionOptionJson = JSON.stringify({
      insuredId,
      folderId,
      amsFileIds,
      isFileUploadFromAMS: isAms,
    });

    const data = new FormData();
    data.append('file',               !isAms ? (file as File) : new Blob([]));
    data.append('ncToken',            ncToken);
    amsFileIds.forEach(id => data.append('amsFileIds', id));
    data.append('folderId',           folderId);
    data.append('insuredId',          insuredId);
    data.append('isFileUploadFromAMS', String(isAms));
    data.append('selectionOptionJson', selectionOptionJson);

    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/ncaicommissionstatement/scans`, data, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(res => {
        const txId = res.data?.responseJson?.transactionId || '';
        setTransactionId(txId);
        setLoading(false);
        setFile(null);
        setSelectedClientFile(null);
        setSuccessMessage(true);
        onScanComplete?.(txId);
      })
      .catch(err => {
        if (err.response?.status === 401) { navigate('/'); return; }
        setErrorMessage('Error while creating the request.');
        setTimeout(() => setErrorMessage(''), 3000);
        setLoading(false);
      });
  }, [activeTab, file, selectedClientFile, navigate, onScanComplete]);

  const canScan = !loading && (activeTab === 'local' ? !!file : !!selectedClientFile);

  return (
    <>
      <div className="cs-main cs-main--flush">
        {/* Tabs */}
        <div className="cs-tabs">
          <button
            className={`cs-tab${activeTab === 'local' ? ' cs-tab--active' : ''}`}
            onClick={() => { setActiveTab('local'); setSuccessMessage(false); setSelectedClientFile(null); }}
          >
            Select Document from Local Drive
          </button>
          <button
            className={`cs-tab${activeTab === 'client' ? ' cs-tab--active' : ''}`}
            onClick={() => { setActiveTab('client'); setSuccessMessage(false); setFile(null); }}
          >
            Select Document from Client Profile
          </button>
        </div>

        {/* Content */}
        <div className="cs-content">
          {successMessage ? (
            <div className="cs-success-banner-wrap">
              <div className="cs-success-banner">
                <div className="cs-success-banner__icon">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="cs-success-banner__body">
                  <p className="cs-success-banner__title">File uploaded successfully!</p>
                  <p className="cs-success-banner__desc">
                    We are processing your file. Please check the{' '}
                    <button className="cs-success-link" onClick={onGoToHistory}>History</button>
                    {' '}tab to find out the file's current progress status and feel free to provide your{' '}
                    <button className="cs-success-link" onClick={openFeedbackPopup}>
                      Feedback here
                    </button>
                  </p>
                </div>
              </div>
            </div>
          ) : activeTab === 'local' ? (
            <div
              className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
            >
              {!file ? (
                <>
                  <div className="cs-dropzone__icon">
                    <FiFileText size={28} />
                  </div>
                  <p className="cs-dropzone__title">Drag & Drop your file here</p>
                  <p className="cs-dropzone__or">or</p>
                  <motion.button
                    className="cs-browse-btn"
                    onClick={() => fileInputRef.current?.click()}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Browse Files
                  </motion.button>
                  <p className="cs-dropzone__types">
                    Allowed file types:{' '}
                    <strong>PDF</strong>,{' '}
                    <strong>Excel (.xls, .xlsx)</strong>, and{' '}
                    <strong>CSV</strong>
                  </p>
                </>
              ) : (
                <div className="cs-file-card">
                  <div className="cs-file-card__icon">
                    <FiFileText size={20} />
                  </div>
                  <div className="cs-file-card__info">
                    <span className="cs-file-card__name">{file.name}</span>
                    <span className="cs-file-card__size">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <button
                    className="cs-file-card__delete"
                    onClick={() => setFile(null)}
                    title="Remove file"
                  >
                    <FiTrash2 size={15} />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                className="cs-hidden-file-input"
                onChange={handleFileInput}
              />
            </div>
          ) : (
            <CommissionScanClientFiles onFileSelected={setSelectedClientFile} />
          )}
        </div>

        {/* Info banner — hidden when success shown */}
        {!successMessage && (
          <div className="cs-info-banner">
            <FiAlertCircle size={14} />
            <span>
              This tool works best with readable PDFs. Please note that scanned or
              image-based PDFs may not be processed. For scanned documents, consider
              converting them to a readable format using OCR tools before uploading.
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="cs-footer">
          <span className="cs-footer__status">
            {errorMessage ? (
              <span className="cs-footer__error-text">{errorMessage}</span>
            ) : !successMessage && (
              activeTab === 'local'
                ? (!file ? 'No files added yet.' : `${file.name} ready to scan`)
                : (!selectedClientFile ? 'No file selected.' : `${selectedClientFile.file.name} ready to scan`)
            )}
          </span>
          <div className="cs-footer__actions">
            {successMessage ? (
              <motion.button
                className="cs-btn-start"
                onClick={handleCancel}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                ✦ New Scan
              </motion.button>
            ) : (
              <>
                <button className="cs-btn-cancel" onClick={handleCancel} disabled={loading}>
                  Cancel
                </button>
                <motion.button
                  className="cs-btn-start"
                  disabled={!canScan}
                  whileHover={{ scale: canScan ? 1.03 : 1 }}
                  whileTap={{ scale: canScan ? 0.97 : 1 }}
                  onClick={createUploadRequest}
                >
                  {loading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
                </motion.button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Feedback popup — opens on same page without navigating */}
      {feedbackAnchor && (
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedBackError   && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={feedbackSubmitting}
              onClick={onSubmitFeedbackClick}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CommissionScanUpload;

